import { useCallback, useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { LogEntry, Person, User } from '@crmsy/shared';
import { ApiError, fetchMe, logout } from './lib/api.js';
import { clearAll, clearReplica, db, getCachedUser, setMeta } from './lib/db.js';
import { syncClient, type ConnectionState } from './lib/sync.js';
import { dispatch, myLastRevertable, worker } from './lib/actions.js';
import { PRIORITY, useKeys } from './lib/keyboard.js';
import { useView } from './lib/router.js';
import { toast } from './lib/toast.js';
import { setLocale, t } from './i18n/index.js';
import { Shell } from './components/Shell.jsx';
import { HelpOverlay } from './components/HelpOverlay.jsx';
import { setTimeZone } from './lib/today.js';
import { AskPanel } from './components/AskPanel.jsx';
import { Toasts } from './components/Toasts.jsx';
import { RevertConfirm } from './components/Dialogs.jsx';
import { usePanelOpen } from './components/ContactPanel.jsx';
import { Picker } from './components/Picker.jsx';
import { adminUsers } from './lib/api.js';
import { getViewAs, setViewAs } from './lib/viewAs.js';
import type { AdminUser } from '@crmsy/shared';
import { Login } from './views/Login.jsx';
import { TableView } from './views/TableView.jsx';
import { FollowUpsView } from './views/FollowUpsView.jsx';
import { NewProspectView } from './views/NewProspectView.jsx';
import { LogView } from './views/LogView.jsx';
import { AdminView } from './views/AdminView.jsx';
import { ProfileView } from './views/ProfileView.jsx';

type Session = { state: 'loading' } | { state: 'anonymous'; offline: boolean } | { state: 'ready'; user: User };

export function App() {
  const [session, setSession] = useState<Session>({ state: 'loading' });
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [help, setHelp] = useState(false);
  const [ask, setAsk] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [view, setView] = useView();
  const pending = useLiveQuery(() => db.outbox.where('status').anyOf('queued', 'sending').count(), [], 0);
  const people = useLiveQuery(() => db.people.toArray(), [], [] as Person[]);
  const [undo, setUndo] = useState<LogEntry | null>(null);
  const [viewAsPicker, setViewAsPicker] = useState<AdminUser[] | null>(null);
  const openViewAs = useCallback(() => {
    adminUsers()
      .then((list) => setViewAsPicker(list))
      .catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), { kind: 'error' }));
  }, []);
  const panelOpen = usePanelOpen();

  // Startup (§5.3): render from the cached user at once, confirm the session in the background.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = await getCachedUser();
      if (cached && !cancelled) setSession({ state: 'ready', user: cached });
      try {
        const me = await fetchMe();
        if (cancelled) return;
        await setMeta('user', me);
        setSession({ state: 'ready', user: me });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          await clearReplica();
          await db.meta.delete('user');
          setSession({ state: 'anonymous', offline: false });
        } else if (!cached) {
          setSession({ state: 'anonymous', offline: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (session.state !== 'ready') return;
    syncClient.onUnauthenticated = () => {
      void clearReplica().then(() => setSession({ state: 'anonymous', offline: false }));
    };
    const unsub = syncClient.subscribe(setConnection);
    syncClient.start();
    void worker.kick();
    return () => {
      unsub();
      syncClient.stop();
    };
  }, [session.state]);

  const onUserUpdated = useCallback((u: User) => {
    void setMeta('user', u);
    setSession({ state: 'ready', user: u });
  }, []);

  const onLogout = useCallback(async () => {
    syncClient.stop();
    try {
      await logout();
    } catch {
      /* offline: the cookie stays, but the local replica goes */
    }
    await clearAll();
    setViewAs(null);
    setSession({ state: 'anonymous', offline: false });
  }, []);

  useKeys(
    {
      '?': () => setHelp((h) => !h),
      q: () => setAsk((a) => !a),
      '1': () => setView('new'),
      '2': () => setView('follow-ups'),
      '3': () => setView('table'),
      '4': () => setView('log'),
      n: () => setView('new'),
      '/': () => searchRef.current?.focus(),
      u: () => {
        void myLastRevertable(user!).then((e) => {
          if (e) setUndo(e);
          else toast(t('toast.nothingToRevert'));
        });
      },
      Escape: () => {
        if (search) {
          setSearch('');
          return;
        }
        return false;
      },
    },
    { priority: PRIORITY.shell, enabled: session.state === 'ready' },
  );

  const user = session.state === 'ready' ? session.user : null;
  // §14: the UI follows the profile locale; bg falls back to en key by key.
  useEffect(() => {
    setLocale(user?.locale === 'bg' ? 'bg' : 'en');
    setTimeZone(user?.timeZone ?? null);
  }, [user?.locale]);
  if (session.state === 'loading') return null;
  if (session.state === 'anonymous' || !user) return <Login offline={session.state === 'anonymous' ? session.offline : false} />;

  let body: React.ReactNode;
  switch (view) {
    case 'new':
      body = <NewProspectView user={user} />;
      break;
    case 'log':
      body = <LogView user={user} search={search} />;
      break;
    case 'profile':
      body = <ProfileView user={user} onUserUpdated={onUserUpdated} />;
      break;
    case 'admin':
      body = user.role === 'admin' ? <AdminView me={user} /> : <ProfileView user={user} onUserUpdated={onUserUpdated} />;
      break;
    case 'table':
      body = <TableView user={user} search={search} setSearch={setSearch} />;
      break;
    case 'follow-ups':
    default:
      body = <FollowUpsView user={user} search={search} setSearch={setSearch} />;
  }

  return (
    <>
      <Shell
        user={user}
        connection={connection}
        pending={pending}
        view={view}
        onView={setView}
        onLogout={onLogout}
        onHelp={() => setHelp(true)}
        onAsk={() => setAsk(true)}
        onViewAs={openViewAs}
        onBackToMe={() => setViewAs(null)}
        search={search}
        setSearch={setSearch}
        searchRef={searchRef}
      >
        {body}
      </Shell>
      {viewAsPicker && (
        <Picker
          title={t('viewAs.title')}
          options={[
            { value: '', label: t('viewAs.me') },
            ...viewAsPicker.filter((u) => u.id !== user.id).map((u) => ({ value: u.id, label: u.notionUserId ? u.name : `${u.name} ${t('viewAs.unmapped')}` })),
          ]}
          selected={[getViewAs()?.userId ?? '']}
          onToggle={(id) => {
            const u = viewAsPicker.find((x) => x.id === id);
            setViewAs(u ? { userId: u.id, name: u.name, notionUserId: u.notionUserId, timeZone: u.timeZone } : null);
          }}
          onClose={() => setViewAsPicker(null)}
        />
      )}
      {help && <HelpOverlay context={panelOpen ? 'record' : 'list'} view={view} onClose={() => setHelp(false)} />}
      {ask && (
        <AskPanel
          onClose={() => setAsk(false)}
          onOpenContact={(name) => {
            setAsk(false);
            setSearch(name);
            setView('table');
          }}
        />
      )}
      {undo && (
        <RevertConfirm
          entry={undo}
          people={people}
          onConfirm={() => void dispatch('revert', { entryId: undo.id }).catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), { kind: 'error' }))}
          onClose={() => setUndo(null)}
        />
      )}
      <Toasts />
    </>
  );
}
